import type { Predicate, PredicateValue as EngineValue } from "@procore/bim-index";

import type { PredicateValue, SearchPredicate } from "apis/procore";

/**
 * The engine's predicate, lowered onto the BIM Query API's wire DTO.
 *
 * `predicate.ts` already turns a `PropertyQuery` into a predicate — synthetic property
 * names, the `IN` value limit and all — and the whole reason it has no I/O in it is that
 * the predicate an index answers is the same whether the files are local or a server is
 * asked. So the served backend reuses that mapping and only lowers the result here,
 * rather than translating `PropertyQuery` a second time.
 *
 * The two are the same wire shape by design: the engine's own type says it "matches the
 * JSON accepted by the CLI and the C# `SearchPredicateDto`", which is what
 * `apis/procore/searchPredicate.ts` mirrors. This module exists rather than a cast
 * because "the same shape today" is exactly the kind of thing that stops being true
 * without anyone noticing, and because it is where an operator the API's DTO has no
 * member for is rejected here instead of being serialized into a `422`.
 */

/**
 * How many values one term may carry before the query has to be split across requests.
 *
 * The service caps an `IN` at 500 values and an `AND`/`OR` branch at 50 children
 * (`PredicateLimits.cs`), and `queryPredicate` spends a wide term as an `OR` of
 * 500-value `IN` chunks — so 500 × 50 values is the widest single term the API will
 * plan, and anything past it is a `400 LIMIT_EXCEEDED` rather than a slow answer. The
 * offline engine has no equivalent ceiling, which is why this lives here rather than in
 * `predicate.ts`.
 */
export const MAX_PREDICATE_VALUES = 25_000;

/**
 * The engine's value type admits `null` where the DTO's does not, though both are plain
 * JSON on the wire and the service binds a `JsonElement`. `queryPredicate` never
 * produces one, so this narrows rather than converts.
 */
function value(from: EngineValue): PredicateValue {
    return from as PredicateValue;
}

/**
 * One predicate as the API's DTO.
 *
 * @throws If the predicate uses an operator the DTO has no member for — the bounds
 * operators, which no `PropertyQuery` can produce and the API does not expose.
 */
export function restPredicate(predicate: Predicate): SearchPredicate {
    switch (predicate.operator) {
        case "IN":
            return {
                operator: "IN",
                property: predicate.property,
                values: predicate.values.map(value),
                negated: predicate.negated,
            };
        case "RANGE":
            return {
                operator: "RANGE",
                property: predicate.property,
                min: value(predicate.min),
                max: value(predicate.max),
                negated: predicate.negated,
            };
        case "DEFINED":
            return { operator: "DEFINED", property: predicate.property, negated: predicate.negated };
        case "EQUAL":
            return {
                operator: "EQUAL",
                property: predicate.property,
                value: value(predicate.value),
                case_sensitive: predicate.case_sensitive,
                negated: predicate.negated,
            };
        case "CONTAINS":
        case "STARTS_WITH":
        case "ENDS_WITH":
            return {
                operator: predicate.operator,
                property: predicate.property,
                value: predicate.value,
                case_sensitive: predicate.case_sensitive,
                negated: predicate.negated,
            };
        case "GREATER_THAN":
        case "LESS_THAN":
        case "GREATER_THAN_OR_EQUAL":
        case "LESS_THAN_OR_EQUAL":
            return {
                operator: predicate.operator,
                property: predicate.property,
                value: value(predicate.value),
                negated: predicate.negated,
            };
        case "SEARCH":
            return { operator: "SEARCH", value: predicate.value, negated: predicate.negated };
        case "AND":
        case "OR":
            return {
                operator: predicate.operator,
                predicates: predicate.predicates.map(restPredicate),
                negated: predicate.negated,
            };
        default:
            throw new Error(`[bim-query] predicate operator ${predicate.operator} has no wire representation`);
    }
}
